import { z } from 'zod'

const statusEnum = z.enum(['PROCESSING', 'WIN', 'LOSE', 'MISSING'])

const statusArraySchema = z
  .union([
    z.array(statusEnum),
    z.string().transform(value =>
      value
        .split(',')
        .map(token => token.trim())
        .filter(Boolean)
    )
  ])
  .optional()
  .transform(value => {
    if (!value || value.length === 0) {
      return undefined
    }
    value.forEach(item => statusEnum.parse(item))
    return value
  })

const keywordSchema = z
  .string()
  .transform(value => {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
  })
  .optional()

const optionalIsoString = z.union([z.string(), z.undefined()]).transform(value => {
  if (typeof value === 'undefined') {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
})

const booleanFromTrueFalse = z.preprocess(value => {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'false') return false
    if (normalized === 'true') return true
  }
  return value
}, z.boolean())

const sortKeyValues = [
  'entryStartedAt',
  'status',
  'text',
  'account',
  'applicantEntryCount',
  'totalCampaignEntryCount',
  'totalCampaignWinningCount'
] as const

export type InstagramInstantwinApplicantSortKey = (typeof sortKeyValues)[number]
export const INSTAGRAM_INSTANTWIN_APPLICANT_SORT_KEYS = sortKeyValues
export const INSTAGRAM_INSTANTWIN_APPLICANT_DEFAULT_PAGE_SIZE = 25

const sortBySchema = z
  .string()
  .default('-entryStartedAt')
  .transform(value => value.trim())
  .refine(
    value => {
      const key = value.startsWith('-') ? value.slice(1) : value
      return (sortKeyValues as readonly string[]).includes(key)
    },
    { message: 'Invalid sort key' }
  )

export const getInstagramInstantwinApplicantsQuerySchema = z.object({
  campaignId: z.coerce.number().int().positive(),
  groupId: z.coerce.number().int().positive().optional(),
  page: z.coerce.number().int().positive().default(1),
  limitCount: z.coerce.number().int().positive().optional(),
  pageSize: z.coerce.number().int().positive().default(INSTAGRAM_INSTANTWIN_APPLICANT_DEFAULT_PAGE_SIZE),
  keyword: keywordSchema,
  status: statusArraySchema,
  prizeId: z.coerce.number().int().positive().optional(),
  entryStartAt: optionalIsoString,
  entryEndAt: optionalIsoString,
  totalCampaignEntryCountMin: z.coerce.number().int().nonnegative().optional(),
  totalCampaignEntryCountMax: z.coerce.number().int().nonnegative().optional(),
  totalCampaignWinningCountMin: z.coerce.number().int().nonnegative().optional(),
  totalCampaignWinningCountMax: z.coerce.number().int().nonnegative().optional(),
  applicantCountMin: z.coerce.number().int().nonnegative().optional(),
  applicantCountMax: z.coerce.number().int().nonnegative().optional(),
  sortBy: sortBySchema,
  forCsv: booleanFromTrueFalse.optional().default(false)
})

export type GetInstagramInstantwinApplicantsQuery = z.infer<typeof getInstagramInstantwinApplicantsQuerySchema>

export const parseGetInstagramInstantwinApplicantsQuery = (input: unknown): GetInstagramInstantwinApplicantsQuery => {
  return getInstagramInstantwinApplicantsQuerySchema.parse(input)
}
